import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { getPackingSlip } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const PrintPackingSlip = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getPackingSlip(id);
        setSlip(s);
        // Auto-trigger print after content paints
        setTimeout(() => window.print(), 500);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load packing slip");
      }
    })();
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-rose-700 font-medium">
        {error}
      </div>
    );
  }
  if (!slip) {
    return (
      <div className="min-h-screen flex items-center justify-center text-yamaha-700 font-medium">
        Loading packing slip…
      </div>
    );
  }

  const sortedItems = [...(slip.items || [])].sort((a, b) =>
    (a.box_no || "").localeCompare(b.box_no || "")
  );

  return (
    <div className="ps-print-root">
      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          .ps-no-print { display: none !important; }
          body { background: white !important; }
        }
        .ps-print-root {
          background: white;
          color: #000;
          font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
          padding: 24px;
          max-width: 800px;
          margin: 0 auto;
          font-size: 12.5px;
          line-height: 1.4;
        }
        .ps-h1 { font-size: 18px; font-weight: 700; letter-spacing: 0.5px; }
        .ps-h2 { font-size: 14px; font-weight: 600; letter-spacing: 0.3px; }
        .ps-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        .ps-table th, .ps-table td {
          border: 1px solid #000;
          padding: 6px 8px;
          text-align: left;
          vertical-align: top;
        }
        .ps-table th {
          background: #f0f0f0;
          font-weight: 700;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .ps-meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px 16px; margin-top: 12px; }
        .ps-meta-row { display: flex; gap: 6px; font-size: 12px; }
        .ps-meta-label { font-weight: 600; min-width: 96px; }
        .ps-divider { border-top: 1.5px solid #000; margin: 12px 0; }
        .ps-summary { display: flex; gap: 40px; margin-top: 10px; font-size: 12.5px; font-weight: 600; }
        .ps-sign-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 28px; margin-top: 48px; }
        .ps-sign-box { border-top: 1px solid #000; padding-top: 4px; font-size: 11px; text-align: center; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .ps-actions { display: flex; gap: 8px; justify-content: flex-end; margin-bottom: 16px; }
        .ps-actions button {
          background: #003399; color: white; padding: 7px 14px; border-radius: 6px;
          font-size: 12.5px; font-weight: 600; border: 0; display: inline-flex; align-items: center; gap: 6px;
          cursor: pointer;
        }
        .ps-actions .ps-back { background: white; color: #1a1b22; border: 1px solid #cfcdd8; }
      `}</style>

      <div className="ps-no-print ps-actions">
        <button className="ps-back" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <button onClick={() => window.print()} data-testid="reprint-btn">
          <Printer style={{ width: 14, height: 14 }} /> Print
        </button>
      </div>

      {/* Header */}
      <div style={{ textAlign: "center" }}>
        <div className="ps-h1">IVA MOTORS YAMAHA</div>
        <div className="ps-h2" style={{ marginTop: 2 }}>YBC PACKING SLIP</div>
        <div style={{ position: "absolute", top: 10, right: 10, textAlign: "center" }} data-testid="ps-qr">
          <QRCodeSVG value={slip.slip_number} size={72} level="M" includeMargin={false} />
          <div style={{ fontSize: 8, marginTop: 2, fontFamily: "monospace" }}>{slip.slip_number}</div>
        </div>
      </div>

      <div className="ps-divider" />

      <div className="ps-meta-grid">
        <div className="ps-meta-row">
          <span className="ps-meta-label">Packing Slip No:</span>
          <span style={{ fontWeight: 700 }}>{slip.slip_number}</span>
        </div>
        <div className="ps-meta-row">
          <span className="ps-meta-label">Invoice No:</span>
          <span style={{ fontWeight: 700 }}>{slip.invoice_no}</span>
        </div>
        <div className="ps-meta-row">
          <span className="ps-meta-label">Date:</span>
          <span>{fmtDate(slip.created_at)}</span>
        </div>
        <div className="ps-meta-row">
          <span className="ps-meta-label">From Location:</span>
          <span>{slip.location_code}</span>
        </div>
        <div className="ps-meta-row">
          <span className="ps-meta-label">To Location:</span>
          <span>{slip.to_location_name || slip.to_location_code || "—"}</span>
        </div>
        <div className="ps-meta-row">
          <span className="ps-meta-label">Created By:</span>
          <span>{slip.created_by || "—"}</span>
        </div>
        <div className="ps-meta-row">
          <span className="ps-meta-label">Status:</span>
          <span>{slip.dispatched ? "DISPATCHED" : "OPEN"}</span>
        </div>
      </div>

      <table className="ps-table">
        <thead>
          <tr>
            <th style={{ width: "10%" }}>S.No</th>
            <th style={{ width: "25%" }}>Part No</th>
            <th style={{ width: "40%" }}>Part Name</th>
            <th style={{ width: "10%", textAlign: "right" }}>Qty</th>
            <th style={{ width: "15%" }}>Box No</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((it, idx) => (
            <tr key={it.id || idx}>
              <td>{idx + 1}</td>
              <td style={{ fontFamily: "monospace" }}>{it.part_number}</td>
              <td>{it.part_name}</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>{it.qty}</td>
              <td style={{ fontFamily: "monospace" }}>{it.box_no}</td>
            </tr>
          ))}
          {sortedItems.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: "center", color: "#444" }}>No items.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ps-summary">
        <div>Total Items: <span style={{ fontWeight: 700 }}>{slip.summary?.total_items || 0}</span></div>
        <div>Total Quantity: <span style={{ fontWeight: 700 }}>{slip.summary?.total_qty || 0}</span></div>
        <div>Total No Of Boxes: <span style={{ fontWeight: 700 }}>{slip.summary?.total_boxes || 0}</span>
          {slip.summary?.box_list?.length ? (
            <span style={{ fontWeight: 400, marginLeft: 6 }}>
              ({slip.summary.box_list.join(", ")})
            </span>
          ) : null}
        </div>
      </div>

      <div className="ps-sign-row">
        <div className="ps-sign-box">Checker 1</div>
        <div className="ps-sign-box">Checker 2</div>
        <div className="ps-sign-box">Security Sign</div>
      </div>

      <div style={{ marginTop: 36, fontSize: 10, textAlign: "center", color: "#444" }}>
        Generated by IVA Motors – Service Sync · {new Date().toLocaleString("en-GB")}
      </div>
    </div>
  );
};

export default PrintPackingSlip;
